"use client";

import React from 'react';
import { formatarData, formatDescription } from "@/lib/utils";

interface SpeakerType {
    documentId?: string;
    name?: string;
    cargo?: string;
    empresa?: string;
    foto?: {
        url?: string;
        alternativeText?: string;
    };
}

interface ICronograma {
    id?: string;
    hora_inicio?: string;
    hora_fim?: string;
    description?: string;
    perfil_speakers?: string;
    cms_speaker?: SpeakerType;
}

interface IAgenda {
    id?: string;
    data?: string;
    cronograma?: ICronograma[];
}

interface EventoAgendaProps {
    agenda: IAgenda[];
    title?: string;
}

const EventoAgenda: React.FC<EventoAgendaProps> = ({ agenda, title = 'Agenda' }) => {
    if (!agenda || agenda.length === 0) {
        return null;
    }

    return (
        <section className=' '>
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 lg:py-20 mb-12 sm:mb-16 lg:mb-20">
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-left mb-6 sm:mb-8 lg:mb-10 uppercase font-exo-2">
                    {title}
                </h2>

                {agenda.map((agendaDay, dayIndex) => (
                    <div key={agendaDay.id || dayIndex} className="mb-12 lg:container lg:px-20">
                        {/* Data da agenda */}
                        {agendaDay.data && (
                            <div className="flex items-center mb-2 px-4 lg:px-0">
                                <div className="w-full sm:w-60 h-14 bg-stone-300/20 rounded-[20px] flex items-center px-6">
                                    <div className="flex items-center gap-3">
                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <g clipPath="url(#clip0_854_16281)">
                                                <path d="M5.6 0V1.6H10.4V0H12V1.6H15.2C15.6418 1.6 16 1.95818 16 2.4V15.2C16 15.6418 15.6418 16 15.2 16H0.8C0.358176 16 0 15.6418 0 15.2V2.4C0 1.95818 0.358176 1.6 0.8 1.6H4V0H5.6ZM14.4 8H1.6V14.4H14.4V8ZM4.8 9.6V11.2H3.2V9.6H4.8ZM8.8 9.6V11.2H7.2V9.6H8.8ZM12.8 9.6V11.2H11.2V9.6H12.8ZM4 3.2H1.6V6.4H14.4V3.2H12V4.8H10.4V3.2H5.6V4.8H4V3.2Z" fill="#FEA115" />
                                            </g>
                                            <defs>
                                                <clipPath id="clip0_854_16281">
                                                    <rect width="16" height="16" fill="white" />
                                                </clipPath>
                                            </defs>
                                        </svg>
                                        <span className="text-zinc-500 text-base sm:text-lg font-bold font-sans">
                                            {(() => {
                                                try {
                                                    return formatarData(agendaDay.data);
                                                } catch {
                                                    return agendaDay.data;
                                                }
                                            })()}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Tabela da agenda com scroll horizontal em mobile */}
                        <div className="relative">
                            {/* Indicador de scroll em dispositivos móveis */}
                            <div className="block sm:hidden mb-2 px-4">
                                <div className="flex items-center gap-2 text-xs text-gray-500">
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M13 7L18 12L13 17M6 7L11 12L6 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    <span>Deslize para ver mais</span>
                                </div>
                            </div>

                            <div className="w-full overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                                {agendaDay.cronograma && agendaDay.cronograma.length > 0 ? (
                                    <div className="space-y-0 min-w-[800px]">
                                        {agendaDay.cronograma.map((item: ICronograma, index: number) => (
                                            <div key={item.id || index}>
                                                <div className="grid grid-cols-12 gap-4 items-center py-6">
                                                    {/* Coluna 1: Ícone do relógio */}
                                                    <div className="col-span-1 flex justify-center">
                                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <g clipPath="url(#clip0_854_16267)">
                                                                <path d="M8 16C3.58172 16 0 12.4182 0 8C0 3.58172 3.58172 0 8 0C12.4182 0 16 3.58172 16 8C16 12.4182 12.4182 16 8 16ZM8 14.4C11.5346 14.4 14.4 11.5346 14.4 8C14.4 4.46538 11.5346 1.6 8 1.6C4.46538 1.6 1.6 4.46538 1.6 8C1.6 11.5346 4.46538 14.4 8 14.4ZM8.8 8H12V9.6H7.2V4H8.8V8Z" fill="#FEA115" />
                                                            </g>
                                                            <defs>
                                                                <clipPath id="clip0_854_16267">
                                                                    <rect width="16" height="16" fill="white" />
                                                                </clipPath>
                                                            </defs>
                                                        </svg>
                                                    </div>

                                                    {/* Coluna 2: Horário */}
                                                    <div className="col-span-2">
                                                        <div className="text-zinc-500 text-sm sm:text-base font-light font-sans whitespace-nowrap">
                                                            {(() => {
                                                                const formatHora = (entrada: string) => {
                                                                    if (!entrada) return '';

                                                                    // Para ISO strings com 'Z' (UTC), converter para horário local de Cabo Verde (UTC-1)
                                                                    // O backend envia em UTC, mas precisamos mostrar no horário local
                                                                    if (entrada.includes('T') && entrada.endsWith('Z')) {
                                                                        const timePart = entrada.split('T')[1];
                                                                        const timeMatch = timePart.match(/^(\d{2}):(\d{2})/);
                                                                        if (timeMatch) {
                                                                            let hours = parseInt(timeMatch[1], 10);
                                                                            const minutes = timeMatch[2];

                                                                            // Subtrair 1 hora para converter UTC para Cabo Verde (UTC-1)
                                                                            hours = hours - 1;
                                                                            if (hours < 0) hours = 23; // Wrap around para meia-noite

                                                                            return `${String(hours).padStart(2, '0')}h${minutes}`;
                                                                        }
                                                                    }

                                                                    // Para ISO strings sem 'Z', extrair diretamente
                                                                    if (entrada.includes('T')) {
                                                                        const timePart = entrada.split('T')[1];
                                                                        const timeMatch = timePart.match(/^(\d{2}):(\d{2})/);
                                                                        if (timeMatch) {
                                                                            return `${timeMatch[1]}h${timeMatch[2]}`;
                                                                        }
                                                                    }

                                                                    // Fallback: padrões como HH:mm, HHhmm
                                                                    const match = entrada.match(/(\d{1,2})[:hH](\d{1,2})/);
                                                                    if (match) {
                                                                        const hh = String(match[1]).padStart(2, '0');
                                                                        const mm = String(match[2] || '00').padStart(2, '0');
                                                                        return `${hh}h${mm}`;
                                                                    }

                                                                    // Caso tenhamos apenas horas (ex: "16")
                                                                    const onlyHour = entrada.match(/^(\d{1,2})$/);
                                                                    if (onlyHour) {
                                                                        const hh = String(onlyHour[1]).padStart(2, '0');
                                                                        return `${hh}h00`;
                                                                    }

                                                                    return '';
                                                                };

                                                                const inicio = item.hora_inicio ? formatHora(item.hora_inicio) : '';
                                                                const fim = item.hora_fim ? formatHora(item.hora_fim) : '';

                                                                if (inicio && fim) {
                                                                    return `${inicio} - ${fim}`;
                                                                } else if (inicio) {
                                                                    return inicio;
                                                                }
                                                                return '-';
                                                            })()}
                                                        </div>
                                                    </div>

                                                    {/* Coluna 3-6: Descrição */}
                                                    <div className="col-span-4">
                                                        <div className="text-black/70 text-sm sm:text-base lg:text-lg font-light font-sans leading-loose">
                                                            {formatDescription(item.description) || '-'}
                                                        </div>
                                                    </div>

                                                    {/* Coluna 7-8: Speaker */}
                                                    <div className="col-span-3">
                                                        {item.cms_speaker?.name ? (
                                                            <div className="flex flex-col gap-1">
                                                                <div className="text-[#F7941D] text-sm sm:text-base lg:text-lg font-semibold font-sans leading-tight">
                                                                    {item.cms_speaker.name}
                                                                </div>
                                                                {item.cms_speaker?.empresa && (
                                                                    <div className="text-black/70 text-xs sm:text-sm font-normal font-sans leading-tight">
                                                                        {item.cms_speaker.empresa}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <div className="text-black/20 text-sm sm:text-base lg:text-lg font-medium font-sans leading-loose">
                                                                -
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Coluna 9-12: Perfil do Speaker */}
                                                    <div className="col-span-2">
                                                        {item.perfil_speakers ? (
                                                            <div className="text-black/70 text-sm sm:text-base lg:text-lg font-normal font-sans leading-loose">
                                                                {item.perfil_speakers}
                                                            </div>
                                                        ) : (
                                                            <div className="text-black/20 text-sm sm:text-base lg:text-lg font-normal font-sans leading-loose">
                                                                -
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Linha separadora */}
                                                {index < (agendaDay.cronograma?.length ?? 0) - 1 && (
                                                    <div className="w-full h-px bg-[#A1A1A1]"></div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="px-4 lg:px-0">
                                        <p className="text-gray-500 italic">Nenhum item de cronograma encontrado para este dia.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
};

export default EventoAgenda;
